import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  Download,
  Plus,
  Quote,
  Sparkles,
  ScrollText,
  ShieldCheck,
  Star,
  BookOpen,
  Compass,
  HeartHandshake,
  Award,
  Briefcase,
  GraduationCap,
  Trophy,
  Brain,
  Rocket,
  Users,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";


/* ─────────────────────────────────────────────────────────────── */
/*  Mock transcript data — wires to Supabase later                 */
/* ─────────────────────────────────────────────────────────────── */

const transcript = {
  learner: {
    name: "Aarav Sharma",
    pronouns: "he / him",
    yearRange: "2022 — 2026",
    location: "Bengaluru, India",
    thesis:
      "I aspire to use behavioural science and AI to help young people in underserved communities discover purpose, build agency, and thrive in the age of artificial intelligence.",
  },
  identity: {
    mission:
      "To design human-centred systems that turn curiosity into contribution — beginning with the learners no one is yet building for.",
    futureIdentity:
      "I hope to contribute to global education access and youth mental wellness through technology, research and ethical product design.",
    careerDNA: ["Builder", "Researcher", "Connector", "Visionary"],
    values: ["Integrity", "Curiosity", "Service", "Craft", "Courage"],
    motivators: ["Mastery", "Impact at scale", "Belonging"],
    interests: ["Behavioural Science", "AI for good", "Public health", "Storytelling"],
    strengths: ["Strategic thinking", "Written communication", "Synthesis", "Listening"],
    personality: "Reflective × Driven · Big-picture × Detail-anchored",
    learningPrefs: "Reading → Building → Teaching",
    growth: "Stretch over comfort. Iterate publicly.",
  },
  intellectual: {
    questions: [
      "How can AI tutoring reach the next billion learners without flattening culture?",
      "Why do people make irrational decisions about their own futures?",
      "What does it mean for an institution to be both ancient and adaptive?",
      "How do we measure character at scale without surveillance?",
    ],
    fascinations: ["Neuroscience", "Sustainability", "Behavioural economics", "History of ideas", "Space exploration"],
    books: [
      { title: "Thinking, Fast and Slow", author: "Daniel Kahneman", reflection: "Cemented why I now sanity-check first instincts before acting on them." },
      { title: "Range", author: "David Epstein", reflection: "Gave me permission to follow more than one calling at a time." },
      { title: "The Anthropocene Reviewed", author: "John Green", reflection: "Taught me to write with care for the reader and the subject." },
      { title: "Man's Search for Meaning", author: "Viktor Frankl", reflection: "Reframed adversity as raw material for purpose." },
    ],
    courses: [
      "CS50x — Harvard Online",
      "Introduction to Psychology — Yale (Coursera)",
      "Behavioural Finance — Duke",
      "AI for Everyone — DeepLearning.AI",
    ],
    research: ["Adaptive learning interfaces", "Adolescent decision-making", "AI literacy in tier-2 Indian schools"],
  },
  character: {
    gratitude: [
      "For Mrs. Iyer, who told me my writing mattered before I believed it did.",
      "For my younger sister, who is teaching me what patience really costs.",
    ],
    challenges: [
      {
        title: "Failing a mock UPSC paper I was certain I had mastered",
        lesson: "Confidence without feedback loops is just performance. I now track what I don't know with the same care as what I do.",
      },
      {
        title: "Running our school's first AI literacy workshop with eight no-shows",
        lesson: "Build the room before the curriculum. Trust precedes attendance.",
      },
    ],
    service: ["120 hrs tutoring underserved learners", "Co-led plastic-free campus initiative", "Weekly visits to a local elder-care home"],
    testimonials: [
      {
        author: "Dr. Meera Iyer",
        role: "English & Debate, DPS Bengaluru",
        quote:
          "Aarav consistently demonstrates intellectual humility. He pursues understanding before he pursues being right. In twenty-two years of teaching, that ordering is rare.",
      },
      {
        author: "Rohan Kapoor",
        role: "Mentor · WeKIT",
        quote:
          "He treats every reflection as a research artefact. He doesn't perform growth — he documents it, examines it, and acts on it.",
      },
    ],
  },
  leadership: {
    initiatives: [
      { title: "Saksham AI Literacy", impact: "240 students reached across 4 schools" },
      { title: "Plastic-Free Campus", impact: "Diverted ~1.8 tonnes of single-use plastic in 2024" },
      { title: "Peer Reflection Circles", impact: "16 weekly cohorts, 90% retention" },
    ],
    metrics: [
      { label: "Students mentored", value: "120" },
      { label: "Trees planted", value: "300" },
      { label: "Funds raised", value: "₹62,500" },
      { label: "Volunteer hours", value: "280" },
    ],
    endorsements: ["Principal · DPS Bengaluru", "President · Rotary Interact", "Founder · Saksham Trust"],
  },
  projects: [
    {
      title: "Saksham — adaptive AI tutor for tier-2 schools",
      problem: "Mass-market ed-tech is built for English-fluent urban learners.",
      whyICared: "My cousins in Bareilly were invisible to every product I admired.",
      whatIDid: "Designed an offline-first WhatsApp tutor in Hindi + English; ran 3 pilots.",
      obstacles: "Power cuts, low-end Android devices, parental skepticism.",
      results: "62% improvement in weekly practice consistency across 80 students.",
      reflection: "Constraints didn't dilute the design — they clarified it.",
      skills: ["Product", "User research", "Prompt engineering", "Hindi UX writing"],
      evidence: ["Pilot report (PDF)", "Demo video", "GitHub repo"],
    },
    {
      title: "Letters to My Future Voter",
      problem: "First-time voters arrive at the booth without civic context.",
      whyICared: "My grandfather's stories of 1977 still shape how I read the news.",
      whatIDid: "Curated 12 oral histories into a free zine; distributed 2,000 copies.",
      obstacles: "Translating across four languages without losing nuance.",
      results: "Adopted by two NGOs as orientation material.",
      reflection: "Stories travel further than statistics when the listener is young.",
      skills: ["Editorial", "Interviewing", "Design", "Distribution"],
      evidence: ["Zine PDF", "Press coverage"],
    },
  ],
  mentoring: [
    { year: "2022", title: "Purpose Discovery", note: "Surfaced curiosity about behavioural science." },
    { year: "2023", title: "Values Exploration", note: "Named integrity and service as load-bearing values." },
    { year: "2024", title: "Career Clarity", note: "Narrowed from 14 careers to 3 evidence-aligned pathways." },
    { year: "2024", title: "Leadership Coaching", note: "Led first multi-school initiative end-to-end." },
    { year: "2025", title: "Capstone Project", note: "Saksham pilots launched; outcomes documented." },
    { year: "2026", title: "University Preparation", note: "Refining narrative for Rhodes-tier applications." },
  ],
  reflections: [
    { prompt: "Biggest lesson this year", answer: "The fastest way to test a belief is to teach it to someone else." },
    { prompt: "A failure that changed me", answer: "Shipping Saksham v1 before user-testing. I now treat assumptions like vulnerabilities." },
    { prompt: "Something I changed my mind about", answer: "That polymathy is indulgent. It's now my edge." },
    { prompt: "A risk I took", answer: "Declined a guaranteed internship to run pilots in three villages." },
    { prompt: "A moment I felt proud", answer: "Watching a student tutor her own sibling using a method I designed." },
    { prompt: "What I want to improve", answer: "Slowing down in conversations. Mining for the unsaid." },
  ],
  credentials: [
    "AI Literacy", "Leadership", "Communication", "Financial Literacy",
    "Climate Literacy", "Entrepreneurship", "Digital Citizenship", "Design Thinking",
  ],
  pathways: [
    {
      tier: "Primary",
      title: "Behavioural Scientist building education products",
      fit: "Marries research instinct with shipping discipline.",
      skills: ["Statistics", "Experimental design", "Product"],
      experiences: ["Research assistantship", "Ed-tech internship"],
      universities: ["Harvard", "Stanford", "Oxford PPE", "Yale Cognitive Science"],
    },
    {
      tier: "Adjacent",
      title: "Public-interest technologist",
      fit: "Same toolkit, civic surface area.",
      skills: ["Policy", "Systems thinking", "Software"],
      experiences: ["Code for India fellowship", "Government internship"],
      universities: ["MIT", "Cambridge", "Ashoka"],
    },
    {
      tier: "Emerging",
      title: "AI ethics researcher",
      fit: "Builds on AI literacy and philosophy reading.",
      skills: ["ML fundamentals", "Ethics frameworks", "Writing"],
      experiences: ["Lab rotation", "Policy fellowship"],
      universities: ["Oxford Internet Institute", "Stanford HAI", "Minerva"],
    },
    {
      tier: "Wild card",
      title: "Long-form journalist on the future of learning",
      fit: "Editorial chops; durable curiosity.",
      skills: ["Narrative", "Reporting", "Synthesis"],
      experiences: ["Newsroom internship", "Self-published series"],
      universities: ["Columbia Journalism", "NYU"],
    },
  ],
  growth: [
    { year: "Grade 8", note: "Shy reader. First poem in school magazine." },
    { year: "Grade 9", note: "Started journaling. Captained junior debate." },
    { year: "Grade 10", note: "Founded Saksham pilot. Mentor matched." },
    { year: "Grade 11", note: "Led 4-school initiative. Began research diaries." },
    { year: "Grade 12", note: "Capstone shipped. Drafting university narrative." },
  ],
  narrative: {
    futureMe:
      "I hope you have stayed curious without becoming cynical. I hope you have remembered that the point was never the résumé.",
    universities:
      "I am not the most decorated applicant you will read this season. I am, I hope, among the most deliberate. Every line in this document is an artefact of something I actually did, examined, and would do again.",
    storySoFar:
      "I grew up between two languages and three cities. I learned early that the most useful question I could ask anyone was: what did you not get to do? That question now organises my work.",
    contribute:
      "I want to build the educational scaffolding I wish my cousins had — research-backed, locally voiced, durable when the internet is not.",
    build:
      "A small, serious institute for adolescent decision-making — half lab, half studio — that publishes openly and ships products for the learners it studies.",
  },
  endorsements: [
    { kind: "Teacher", name: "Dr. Meera Iyer", quote: "An unusually self-edited mind. Writes to think, not to impress." },
    { kind: "Mentor", name: "Rohan Kapoor", quote: "Documents his growth like a researcher. Acts on it like a founder." },
    { kind: "Peer", name: "Ananya Rao", quote: "The person I call when I need an honest second draft." },
    { kind: "Parent", name: "Priya Sharma", quote: "He chooses harder. Always has." },
    { kind: "Community leader", name: "Sarpanch · Village Pilot", quote: "He returned. That is the test most don't pass." },
  ],
};

const SECTIONS = [
  { id: "identity",     numeral: "I",    title: "Identity & Purpose",        q: "Who am I becoming?" },
  { id: "intellectual", numeral: "II",   title: "Intellectual Vitality",     q: "What excites this learner intellectually?" },
  { id: "character",    numeral: "III",  title: "Character & Values",        q: "What kind of person shows up when no one is watching?" },
  { id: "leadership",   numeral: "IV",   title: "Leadership & Impact",       q: "What has changed because this learner was here?" },
  { id: "projects",     numeral: "V",    title: "Projects & Experiences",    q: "What has this learner made, and what did making it teach them?" },
  { id: "mentoring",    numeral: "VI",   title: "Mentoring Journey",         q: "Who has shaped this learner, and how?" },
  { id: "reflections",  numeral: "VII",  title: "Reflection Archive",        q: "How does this learner think about themselves?" },
  { id: "credentials",  numeral: "VIII", title: "WeCred Credentials",       q: "What has been independently verified?" },
  { id: "pathways",     numeral: "IX",   title: "Future Pathways",           q: "What futures does the evidence open?" },
  { id: "growth",       numeral: "X",    title: "Personal Growth Timeline",  q: "How has this learner evolved?" },
  { id: "narrative",    numeral: "XI",   title: "Signature Narrative",       q: "What is the story this learner would tell in their own voice?" },
  { id: "endorsements", numeral: "XII",  title: "Endorsements",              q: "Who vouches for this learner — and what do they see?" },
  { id: "signature",    numeral: "XIII", title: "WeKIT Signature Transcript", q: "If reduced to a single document, what does this learner's life so far amount to?" },
];

/* ─────────────────────────────────────────────────────────────── */
/*  Presentation primitives                                        */
/* ─────────────────────────────────────────────────────────────── */

function SectionShell({
  id, numeral, title, question, children,
}: { id: string; numeral: string; title: string; question: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 py-16 sm:py-24 border-t border-border/50 first:border-t-0">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-baseline gap-4 mb-3">
          <span className="font-display text-5xl sm:text-6xl font-bold text-primary/30 tabular-nums leading-none">
            {numeral}
          </span>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Section {numeral}
          </div>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
          {title}
        </h2>
        <p className="mt-3 text-base sm:text-lg text-muted-foreground italic font-serif">
          “{question}”
        </p>
        <Separator className="my-8" />
        <div className="space-y-8">{children}</div>
      </div>
    </section>
  );
}

function NarrativeBlock({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div>
      {label && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
          {label}
        </div>
      )}
      <div className="font-serif text-lg sm:text-xl leading-relaxed text-foreground/90">
        {children}
      </div>
    </div>
  );
}

function QuoteCard({ quote, author, role }: { quote: string; author: string; role?: string }) {
  return (
    <figure className="border-l-2 border-primary/40 pl-5 py-1">
      <Quote className="h-4 w-4 text-primary/50 mb-2" />
      <blockquote className="font-serif text-lg leading-relaxed text-foreground/90">
        {quote}
      </blockquote>
      <figcaption className="mt-3 text-xs text-muted-foreground">
        — <span className="font-semibold text-foreground">{author}</span>
        {role && <> · {role}</>}
      </figcaption>
    </figure>
  );
}

function MetaPair({ label, value }: { label: string; value: string | string[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
        {label}
      </div>
      {Array.isArray(value) ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <Badge key={v} variant="outline" className="rounded-full font-normal">
              {v}
            </Badge>
          ))}
        </div>
      ) : (
        <div className="text-sm text-foreground">{value}</div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Page                                                           */
/* ─────────────────────────────────────────────────────────────── */

export default function Wap3Portfolio() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const location = useLocation();
  const isPortfolio = location.pathname === "/portfolio";
  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0]?.id ?? "");

  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const displayName = useMemo(() => {
    return (user?.user_metadata?.full_name as string) || transcript.learner.name;
  }, [user]);

  function exportTranscript() {
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) {
      toast({ title: "Couldn't open print window", description: "Please allow pop-ups for this site.", variant: "destructive" });
      return;
    }
    win.document.write(`<!doctype html><html><head><title>Human Potential Transcript — ${displayName}</title>
<style>
  @page { size: A4; margin: 24mm; }
  body { font-family: Georgia, 'Iowan Old Style', serif; color:#180205; line-height:1.55; max-width: 720px; margin:0 auto; padding:48px 32px; }
  h1 { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: 38px; margin:0 0 4px; letter-spacing:-0.02em; }
  h2 { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: 22px; margin:48px 0 8px; letter-spacing:-0.01em; }
  h3 { font-size: 14px; text-transform: uppercase; letter-spacing:0.15em; color:#666; margin: 32px 0 8px; font-family: 'Inter', system-ui, sans-serif; }
  p { margin: 0 0 12px; }
  .meta { color:#666; font-size:13px; margin-bottom:32px; }
  .thesis { font-size:18px; font-style:italic; border-left:3px solid #00665B; padding-left:16px; margin:24px 0; }
  .pill { display:inline-block; border:1px solid #ddd; border-radius:999px; padding:2px 10px; font-size:11px; margin:0 4px 4px 0; font-family: 'Inter', sans-serif; }
  blockquote { font-style:italic; border-left:2px solid #00665B; padding-left:14px; color:#333; }
  hr { border:none; border-top:1px solid #eee; margin: 40px 0; }
</style></head><body>
<h1>${displayName}</h1>
<div class="meta">Human Potential Transcript · ${transcript.learner.yearRange} · ${transcript.learner.location}</div>
<div class="thesis">${transcript.learner.thesis}</div>
<hr />
<h2>I. Identity &amp; Purpose</h2>
<p><strong>Mission.</strong> ${transcript.identity.mission}</p>
<p><strong>Future identity.</strong> ${transcript.identity.futureIdentity}</p>
<h3>Career DNA</h3>${transcript.identity.careerDNA.map(c=>`<span class="pill">${c}</span>`).join("")}
<h3>Values</h3>${transcript.identity.values.map(c=>`<span class="pill">${c}</span>`).join("")}
<h2>II. Intellectual Vitality</h2>
${transcript.intellectual.questions.map(q=>`<p>· ${q}</p>`).join("")}
<h3>Fascinations</h3>${transcript.intellectual.fascinations.map(c=>`<span class="pill">${c}</span>`).join("")}
<h2>III. Character &amp; Values</h2>
${transcript.character.testimonials.map(t=>`<blockquote>${t.quote}<br/><small>— ${t.author}, ${t.role}</small></blockquote>`).join("")}
<h2>IV. Leadership &amp; Impact</h2>
${transcript.leadership.initiatives.map(i=>`<p><strong>${i.title}.</strong> ${i.impact}</p>`).join("")}
<h2>V. Projects &amp; Experiences</h2>
${transcript.projects.map(p=>`<p><strong>${p.title}.</strong> ${p.results} <em>${p.reflection}</em></p>`).join("")}
<h2>VI. Mentoring Journey</h2>
${transcript.mentoring.map(m=>`<p><strong>${m.year} · ${m.title}.</strong> ${m.note}</p>`).join("")}
<h2>VII. Reflection Archive</h2>
${transcript.reflections.map(r=>`<p><strong>${r.prompt}.</strong> ${r.answer}</p>`).join("")}
<h2>VIII. WeCred Credentials</h2>
${transcript.credentials.map(c=>`<span class="pill">${c}</span>`).join("")}
<h2>IX. Future Pathways</h2>
${transcript.pathways.map(p=>`<p><strong>${p.tier}: ${p.title}.</strong> ${p.fit}</p>`).join("")}
<h2>X. Personal Growth Timeline</h2>
${transcript.growth.map(g=>`<p><strong>${g.year}.</strong> ${g.note}</p>`).join("")}
<h2>XI. Signature Narrative</h2>
<h3>To universities</h3><p>${transcript.narrative.universities}</p>
<h3>My story so far</h3><p>${transcript.narrative.storySoFar}</p>
<h3>What I hope to build</h3><p>${transcript.narrative.build}</p>
<h2>XII. Endorsements</h2>
${transcript.endorsements.map(e=>`<blockquote>${e.quote}<br/><small>— ${e.name}, ${e.kind}</small></blockquote>`).join("")}
<hr/>
<p style="text-align:center;color:#666;font-size:11px">Issued by WeKIT · WAP³ Human Potential Transcript</p>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 350);
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>WAP³ — Human Potential Transcript · {displayName}</title>
        <meta name="description" content="A curated lifelong record of identity, intellectual vitality, character, leadership and pathways." />
      </Helmet>

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button asChild variant="ghost" size="sm" className="-ml-2 shrink-0">
              <Link to="/dashboard"><ArrowLeft className="h-4 w-4 mr-1.5" /> Dashboard</Link>
            </Button>
            <Link to="/" className="hidden sm:flex items-center gap-2 group">
              <WeKitLogo size="sm" clickable={false} />
              <div className="leading-tight">
                <div className="font-display text-sm font-bold tracking-tight group-hover:text-primary transition-colors">
                  WeKIT™
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  WAP³ Portfolio
                </div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEvidenceOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Add evidence
            </Button>
            <Button size="sm" onClick={exportTranscript} className="rounded-full">
              <Download className="h-4 w-4 mr-1.5" /> Export Transcript
            </Button>
          </div>
        </div>
      </header>

      {/* Layout: rail + body */}
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[220px_1fr] gap-12">
        {/* Sticky section rail (desktop) */}
        <nav className="hidden lg:block pt-32" aria-label="Transcript sections">
          <div className="sticky top-24 space-y-4">
            {/* Brand / Home link */}
            <Link
              to="/portfolio"
              aria-current={isPortfolio ? "page" : undefined}
              onClick={(e) => {
                if (isPortfolio) {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setActiveSection(SECTIONS[0]?.id ?? "");
                }
              }}
              className={`group relative flex items-start gap-2.5 pb-4 border-b transition-colors ${
                isPortfolio
                  ? "border-primary/40"
                  : "border-border/60 hover:border-primary/30"
              }`}
            >
              {isPortfolio && (
                <span
                  aria-hidden
                  className="absolute -left-3 top-0.5 h-5 w-[2px] rounded-full bg-primary"
                />
              )}
              <ScrollText
                className={`h-4 w-4 mt-0.5 shrink-0 ${
                  isPortfolio ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                }`}
              />
              <div className="min-w-0">
                <div
                  className={`text-[11px] uppercase tracking-[0.18em] font-semibold leading-tight ${
                    isPortfolio ? "text-primary" : "text-foreground/80 group-hover:text-primary"
                  }`}
                >
                  My Portfolio
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 leading-snug group-hover:text-foreground transition-colors">
                  Human Potential Transcript
                </div>
              </div>
            </Link>

            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
                Contents
              </div>
              {SECTIONS.map((s) => {
                const active = activeSection === s.id;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    aria-current={active ? "location" : undefined}
                    onClick={() => setActiveSection(s.id)}
                    className={`relative flex items-baseline gap-2 text-xs py-1 pl-2 rounded-sm transition-colors ${
                      active
                        ? "text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {active && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-primary"
                      />
                    )}
                    <span
                      className={`font-display font-semibold w-8 tabular-nums ${
                        active ? "text-primary" : "text-primary/70"
                      }`}
                    >
                      {s.numeral}
                    </span>
                    <span className="leading-tight">{s.title}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </nav>



        {/* Body */}
        <main>
          {/* ───── Coming-soon notice ───── */}
          <div
            role="status"
            className="mt-6 mb-2 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm"
          >
            <Badge className="bg-amber-400 text-amber-950 hover:bg-amber-400 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              Coming Soon
            </Badge>
            <p className="text-amber-100/90 leading-snug">
              The Human Potential Transcript is in active development. Everything below is a{" "}
              <span className="font-semibold text-amber-50">sample preview</span> showing how
              your real transcript will look once your data flows in.
            </p>
          </div>

          {/* ───── Cover ───── */}
          <section className="pt-16 sm:pt-24 pb-12 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <WeKitLogo size="sm" clickable={false} />
              <div className="flex items-center gap-2">
                <span className="font-display text-sm font-bold tracking-tight">WeKIT™</span>
                <span className="text-muted-foreground">·</span>
                <ScrollText className="h-3.5 w-3.5 text-primary" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  Human Potential Transcript
                </span>
              </div>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl font-bold tracking-tight leading-[1.02]">
              {displayName}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>{transcript.learner.pronouns}</span>
              <span>·</span>
              <span>{transcript.learner.yearRange}</span>
              <span>·</span>
              <span>{transcript.learner.location}</span>
            </div>
            <p className="mt-10 font-serif text-2xl sm:text-3xl leading-snug text-foreground/90 border-l-2 border-primary/50 pl-6">
              {transcript.learner.thesis}
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="rounded-full gap-1.5 py-1.5 px-3 border-primary/40 text-primary">
                <ShieldCheck className="h-3 w-3" /> Verified by WeKIT
              </Badge>
              <Badge variant="outline" className="rounded-full gap-1.5 py-1.5 px-3">
                <Sparkles className="h-3 w-3" /> Continuously curated
              </Badge>
              <Badge variant="outline" className="rounded-full gap-1.5 py-1.5 px-3">
                <Star className="h-3 w-3" /> Submission-ready
              </Badge>
            </div>
            <p className="mt-10 text-sm text-muted-foreground italic max-w-xl">
              The guiding question of this transcript is not <em className="text-foreground">"What did this learner achieve?"</em> but{" "}
              <em className="text-foreground">"Who is this learner becoming, and what evidence supports that story?"</em>
            </p>
          </section>

          {/* ───── I. Identity & Purpose ───── */}
          <SectionShell id="identity" numeral="I" title="Identity & Purpose" question="Who am I becoming?">
            <NarrativeBlock label="Personal mission">{transcript.identity.mission}</NarrativeBlock>
            <NarrativeBlock label="Future identity">{transcript.identity.futureIdentity}</NarrativeBlock>
            <div className="grid sm:grid-cols-2 gap-6 pt-2">
              <MetaPair label="Career DNA" value={transcript.identity.careerDNA} />
              <MetaPair label="Core values" value={transcript.identity.values} />
              <MetaPair label="Motivators" value={transcript.identity.motivators} />
              <MetaPair label="Interests" value={transcript.identity.interests} />
              <MetaPair label="Strengths" value={transcript.identity.strengths} />
              <MetaPair label="Personality dynamics" value={transcript.identity.personality} />
              <MetaPair label="Learning preferences" value={transcript.identity.learningPrefs} />
              <MetaPair label="Growth orientation" value={transcript.identity.growth} />
            </div>
          </SectionShell>

          {/* ───── II. Intellectual Vitality ───── */}
          <SectionShell id="intellectual" numeral="II" title="Intellectual Vitality" question="What excites this learner intellectually?">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
                Questions I keep returning to
              </div>
              <ul className="space-y-3">
                {transcript.intellectual.questions.map((q) => (
                  <li key={q} className="flex gap-3 font-serif text-lg leading-relaxed">
                    <span className="text-primary mt-1.5">·</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
            <MetaPair label="Areas of fascination" value={transcript.intellectual.fascinations} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Books read</div>
              <div className="space-y-4">
                {transcript.intellectual.books.map((b) => (
                  <div key={b.title} className="border-l border-border pl-4">
                    <div className="font-semibold">{b.title} <span className="text-muted-foreground font-normal">— {b.author}</span></div>
                    <div className="text-sm text-muted-foreground italic mt-1">{b.reflection}</div>
                  </div>
                ))}
              </div>
            </div>
            <MetaPair label="Courses completed" value={transcript.intellectual.courses} />
            <MetaPair label="Research interests" value={transcript.intellectual.research} />
          </SectionShell>

          {/* ───── III. Character & Values ───── */}
          <SectionShell id="character" numeral="III" title="Character & Values" question="What kind of person shows up when no one is watching?">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Gratitude</div>
              <ul className="space-y-2 font-serif text-base leading-relaxed text-foreground/90">
                {transcript.character.gratitude.map((g) => <li key={g}>· {g}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Challenges overcome</div>
              <div className="space-y-4">
                {transcript.character.challenges.map((c) => (
                  <div key={c.title}>
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-sm text-muted-foreground italic mt-1">Lesson: {c.lesson}</div>
                  </div>
                ))}
              </div>
            </div>
            <MetaPair label="Acts of service" value={transcript.character.service} />
            <div className="space-y-6 pt-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Mentor testimonials</div>
              {transcript.character.testimonials.map((t) => (
                <QuoteCard key={t.author} quote={t.quote} author={t.author} role={t.role} />
              ))}
            </div>
          </SectionShell>

          {/* ───── IV. Leadership & Impact ───── */}
          <SectionShell id="leadership" numeral="IV" title="Leadership & Impact" question="What has changed because this learner was here?">
            <div className="space-y-4">
              {transcript.leadership.initiatives.map((i) => (
                <div key={i.title} className="flex items-start gap-4">
                  <Trophy className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{i.title}</div>
                    <div className="text-sm text-muted-foreground">{i.impact}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
              {transcript.leadership.metrics.map((m) => (
                <div key={m.label} className="border border-border/60 rounded-2xl p-4 text-center">
                  <div className="font-display text-3xl font-bold text-primary">{m.value}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{m.label}</div>
                </div>
              ))}
            </div>
            <MetaPair label="Endorsements" value={transcript.leadership.endorsements} />
          </SectionShell>

          {/* ───── V. Projects & Experiences ───── */}
          <SectionShell id="projects" numeral="V" title="Projects & Experiences" question="What has this learner made, and what did making it teach them?">
            <div className="space-y-8">
              {transcript.projects.map((p) => (
                <Card key={p.title} className="border-border/60">
                  <CardContent className="p-6 sm:p-8 space-y-4">
                    <h3 className="font-display text-2xl font-bold tracking-tight">{p.title}</h3>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                      <div><span className="text-muted-foreground">Problem · </span>{p.problem}</div>
                      <div><span className="text-muted-foreground">Why I cared · </span>{p.whyICared}</div>
                      <div><span className="text-muted-foreground">What I did · </span>{p.whatIDid}</div>
                      <div><span className="text-muted-foreground">Obstacles · </span>{p.obstacles}</div>
                      <div className="sm:col-span-2"><span className="text-muted-foreground">Results · </span>{p.results}</div>
                    </div>
                    <blockquote className="font-serif italic text-base border-l-2 border-primary/40 pl-4 text-foreground/90">
                      {p.reflection}
                    </blockquote>
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {p.skills.map((s) => <Badge key={s} variant="outline" className="rounded-full font-normal">{s}</Badge>)}
                    </div>
                    <div className="text-xs text-muted-foreground">Evidence: {p.evidence.join(" · ")}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </SectionShell>

          {/* ───── VI. Mentoring Journey ───── */}
          <SectionShell id="mentoring" numeral="VI" title="Mentoring Journey" question="Who has shaped this learner, and how?">
            <ol className="relative border-l-2 border-primary/30 ml-2 space-y-6">
              {transcript.mentoring.map((m) => (
                <li key={m.year + m.title} className="pl-6 relative">
                  <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{m.year}</div>
                  <div className="font-display text-lg font-semibold mt-0.5">{m.title}</div>
                  <div className="text-sm text-muted-foreground mt-1 max-w-xl">{m.note}</div>
                </li>
              ))}
            </ol>
          </SectionShell>

          {/* ───── VII. Reflection Archive ───── */}
          <SectionShell id="reflections" numeral="VII" title="Reflection Archive" question="How does this learner think about themselves?">
            <div className="space-y-6">
              {transcript.reflections.map((r) => (
                <div key={r.prompt}>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{r.prompt}</div>
                  <p className="font-serif text-lg leading-relaxed mt-1.5 text-foreground/90">{r.answer}</p>
                </div>
              ))}
            </div>
            <Card className="bg-primary/[0.04] border-primary/20 mt-4">
              <CardContent className="p-5 flex gap-3">
                <Brain className="h-4 w-4 text-primary mt-1 shrink-0" />
                <div className="text-sm text-foreground/80">
                  <span className="font-semibold">AI pattern summary · </span>
                  This learner's reflections show consistent self-correction, an unusual willingness to publish unfinished thinking, and a recurring orientation toward teaching as a way of understanding.
                </div>
              </CardContent>
            </Card>
          </SectionShell>

          {/* ───── VIII. WeCred Credentials ───── */}
          <SectionShell id="credentials" numeral="VIII" title="WeCred Credentials" question="What has been independently verified?">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {transcript.credentials.map((c) => (
                <div key={c} className="border border-border/60 rounded-xl p-4 text-center">
                  <Award className="h-4 w-4 text-primary mx-auto mb-2" />
                  <div className="text-xs font-semibold">{c}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground italic">
              Credentials are presented in service of the narrative — never as the headline.
            </p>
          </SectionShell>

          {/* ───── IX. Future Pathways ───── */}
          <SectionShell id="pathways" numeral="IX" title="Future Pathways" question="What futures does the evidence open?">
            <div className="space-y-5">
              {transcript.pathways.map((p) => (
                <Card key={p.tier} className="border-border/60">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{p.tier}</div>
                        <div className="font-display text-xl font-semibold mt-1">{p.title}</div>
                      </div>
                      <Compass className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <p className="mt-3 font-serif italic text-foreground/80">{p.fit}</p>
                    <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Skills</div>
                        {p.skills.join(", ")}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Experiences</div>
                        {p.experiences.join(", ")}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Universities</div>
                        {p.universities.join(", ")}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </SectionShell>

          {/* ───── X. Personal Growth Timeline ───── */}
          <SectionShell id="growth" numeral="X" title="Personal Growth Timeline" question="How has this learner evolved?">
            <div className="grid sm:grid-cols-5 gap-4">
              {transcript.growth.map((g) => (
                <div key={g.year} className="border-t-2 border-primary/40 pt-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold">{g.year}</div>
                  <div className="text-sm mt-1.5 text-foreground/85">{g.note}</div>
                </div>
              ))}
            </div>
          </SectionShell>

          {/* ───── XI. Signature Narrative ───── */}
          <SectionShell id="narrative" numeral="XI" title="Signature Narrative" question="What is the story this learner would tell in their own voice?">
            <NarrativeBlock label="A letter to my future self">{transcript.narrative.futureMe}</NarrativeBlock>
            <NarrativeBlock label="A letter to the admissions committee">{transcript.narrative.universities}</NarrativeBlock>
            <NarrativeBlock label="My story so far">{transcript.narrative.storySoFar}</NarrativeBlock>
            <NarrativeBlock label="Why I want to contribute">{transcript.narrative.contribute}</NarrativeBlock>
            <NarrativeBlock label="What I hope to build">{transcript.narrative.build}</NarrativeBlock>
          </SectionShell>

          {/* ───── XII. Endorsements ───── */}
          <SectionShell id="endorsements" numeral="XII" title="Endorsements" question="Who vouches for this learner — and what do they see?">
            <div className="space-y-6">
              {transcript.endorsements.map((e) => (
                <div key={e.name}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{e.kind}</div>
                  <QuoteCard quote={e.quote} author={e.name} role={e.kind} />
                </div>
              ))}
            </div>
          </SectionShell>

          {/* ───── XIII. Signature Transcript ───── */}
          <SectionShell id="signature" numeral="XIII" title="WeKIT Signature Transcript" question="If reduced to a single document, what does this learner's life so far amount to?">
            <p className="font-serif text-lg leading-relaxed text-foreground/90">
              The Signature Transcript brings every section above together — designed to accompany
              applications to Harvard, Stanford, Oxford, Cambridge, Yale, MIT, Ashoka, Minerva and the Rhodes
              Scholarship. It is not a résumé. It is a curated record of who this learner is becoming.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={exportTranscript} size="lg" className="rounded-full">
                <Download className="h-4 w-4 mr-2" /> Export Human Potential Transcript
              </Button>
              <Button variant="outline" size="lg" className="rounded-full" onClick={() => setEvidenceOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Add new evidence
              </Button>
            </div>
            <div className="text-xs text-muted-foreground pt-6 italic">
              Issued by WeKIT · WAP³ Human Potential Transcript · {transcript.learner.yearRange}
            </div>
          </SectionShell>

          <footer className="py-16 text-center text-xs text-muted-foreground border-t border-border/40 mt-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <WeKitLogo size="sm" clickable={false} />
              <span className="font-display text-sm font-bold tracking-tight text-foreground">WeKIT™</span>
            </div>
            <div className="font-semibold text-foreground/80">WAP³ — WeKIT Actual Purpose &amp; Pathways Portfolio</div>
            <div className="mt-1">Human Potential Transcript · Issued by WeKIT™</div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.18em]">wekit.ai</div>
          </footer>
        </main>
      </div>

      {/* Add evidence dialog (lightweight) */}
      <AddEvidenceDialog open={evidenceOpen} onOpenChange={setEvidenceOpen} />
    </div>
  );
}

function AddEvidenceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [reflection, setReflection] = useState("");

  function submit() {
    if (!title.trim()) {
      toast({ title: "Add a title", description: "Every piece of evidence needs a name.", variant: "destructive" });
      return;
    }
    toast({ title: "Evidence captured", description: "It will appear in your transcript shortly." });
    setTitle(""); setReflection("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add evidence to your transcript</DialogTitle>
          <DialogDescription>
            Activities, projects, reflections, mentor observations — anything that supports the story you're becoming.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Led plastic-free campus initiative" />
          </div>
          <div>
            <Label>Reflection</Label>
            <Textarea value={reflection} onChange={(e) => setReflection(e.target.value)} placeholder="What did you do, and what did it teach you?" className="min-h-28" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}>Add to transcript</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
